import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import {
  IndianRupee, Warehouse, Landmark, ArrowRight,
  TrendingDown, MapPin, CheckCircle2,
} from "lucide-react";

function Reveal({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

const headingStyle = {
  fontFamily: "'Barlow Condensed', sans-serif",
  fontWeight: 800,
  fontSize: "clamp(2rem, 3vw, 2.8rem)",
  textTransform: "uppercase",
  lineHeight: 1.1,
};

const priceComparison = [
  { hub: "IIE Sigaddi, Kotdwar (VSINDLPARK)", price: "₹7,500", note: "SIIDCUL / SIIDCUL, 90-yr lease", highlight: true },
  { hub: "IMT Kharkhoda, Delhi NCR", price: "₹39,200 – ₹60,000", note: "HSIIDC allotment / resale" },
  { hub: "Tronica City, Ghaziabad NCR", price: "₹35,000 – ₹45,000 /sq.yd", note: "UPSIDA developed" },
  { hub: "Meerut IMLC (UPEIDA)", price: "₹11,470+", note: "Ganga Expressway node" },
];

const incentives = [
  "Capital investment subsidy on plant, machinery & building",
  "Interest subsidy on term loans for eligible units",
  "100% stamp duty exemption on the industrial land deed",
  "Net SGST reimbursement on eligible sales for a defined period",
  "Up to 20% capital subsidy for warehousing under the Uttarakhand Logistics Policy 2023",
  "MSME-focused benefits and single-window clearance via SIIDCUL",
];

export function SeoContentSections() {
  return (
    <>
      {/* ═══════ WHY KOTDWAR vs DELHI NCR — price advantage ═══════ */}
      <section className="relative py-24 border-t border-white/10" data-testid="seo-price-comparison-section">
        <div className="absolute inset-0 blueprint-grid opacity-10 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <Reveal>
            <span className="overline inline-flex items-center gap-2">
              <TrendingDown className="w-3.5 h-3.5 text-orange-500" /> Price Advantage
            </span>
            <div className="section-divider-left mt-3" />
            <h2 className="text-white mt-4" style={headingStyle}>
              Why Kotdwar Beats Delhi NCR on Industrial Land Price
            </h2>
            <p className="text-slate-400 text-sm mt-4 max-w-3xl leading-relaxed">
              Industrial plots at the SIIDCUL / SIIDCUL <strong className="text-slate-200">Sigaddi Growth Centre, Kotdwar</strong> cost a
              fraction of comparable factory land near Delhi NCR — while keeping you connected to the same North India markets
              via road, rail and the improving Delhi–Dehradun corridor. Here is how the price per square metre compares.
            </p>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="mt-10 overflow-x-auto border border-white/[0.08]">
              <table className="w-full text-left" data-testid="price-comparison-table">
                <thead>
                  <tr className="bg-slate-900/70 text-slate-400 text-xs uppercase tracking-wider" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                    <th className="px-5 py-4 font-medium">Industrial Hub</th>
                    <th className="px-5 py-4 font-medium">Price / sq.mtr</th>
                    <th className="px-5 py-4 font-medium hidden md:table-cell">Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.06]">
                  {priceComparison.map((row, i) => (
                    <tr
                      key={i}
                      className={row.highlight ? "bg-orange-500/[0.07]" : "hover:bg-white/[0.02] transition-colors"}
                      data-testid={`price-row-${i}`}
                    >
                      <td className="px-5 py-4">
                        <span className={`text-sm font-semibold ${row.highlight ? "text-orange-400" : "text-slate-200"}`}>
                          {row.hub}
                        </span>
                      </td>
                      <td className={`px-5 py-4 text-sm font-bold ${row.highlight ? "text-orange-400" : "text-slate-300"}`} style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                        {row.price}
                      </td>
                      <td className="px-5 py-4 text-xs text-slate-500 hidden md:table-cell">{row.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-slate-600 text-xs mt-3" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
              * Indicative market ranges for comparison. Final VSINDLPARK pricing depends on plot size, road frontage & corner location.
            </p>
            <Link
              to="/kotdwar-vs-delhi-ncr"
              className="inline-flex items-center gap-2 mt-6 text-sm text-orange-500 font-semibold hover:text-orange-400 transition-colors"
              data-testid="price-comparison-cta"
            >
              See the full Kotdwar vs Delhi NCR comparison <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ═══════ WAREHOUSE & LOGISTICS PARK PLOTS ═══════ */}
      <section className="relative py-24 border-t border-white/10 bg-slate-900/40" data-testid="seo-warehouse-logistics-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <Reveal>
                <span className="overline inline-flex items-center gap-2">
                  <Warehouse className="w-3.5 h-3.5 text-orange-500" /> Warehouse & Logistics
                </span>
                <div className="section-divider-left mt-3" />
                <h2 className="text-white mt-4" style={headingStyle}>
                  Warehouse & Logistics Park Plots in Uttarakhand
                </h2>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="text-slate-400 text-sm mt-5 leading-relaxed">
                  Building a distribution centre, cold storage or logistics park? Plots at IIE Sigaddi, Kotdwar are ideally
                  positioned to serve Delhi-NCR, Dehradun, Haridwar and the Uttarakhand hills. Warehousing and logistics
                  are permitted uses at the SIIDCUL Sigaddi Growth Centre, with dedicated policy support.
                </p>
              </Reveal>
              <Reveal delay={0.18}>
                <p className="text-slate-400 text-sm mt-4 leading-relaxed">
                  Under the <strong className="text-slate-200">Uttarakhand Logistics Policy 2023</strong>, eligible warehousing
                  and logistics projects can claim up to a <strong className="text-orange-400">20% capital subsidy</strong>,
                  with cold storage facilities receiving an additional benefit over central assistance — dramatically improving
                  project economics versus renting warehouse space near Delhi.
                </p>
              </Reveal>
              <Reveal delay={0.26}>
                <div className="flex flex-wrap gap-3 mt-8">
                  <Link
                    to="/plots"
                    className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2.5 font-bold uppercase tracking-wider text-sm transition-all duration-300 flex items-center gap-2 hover:shadow-[0_0_20px_rgba(249,115,22,0.3)]"
                    data-testid="warehouse-view-plots-btn"
                  >
                    View Warehouse Plots <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link
                    to="/schemes"
                    className="border border-white/20 text-white hover:border-orange-500 hover:text-orange-400 px-6 py-2.5 font-bold uppercase tracking-wider text-sm transition-all duration-300"
                    data-testid="warehouse-view-schemes-btn"
                  >
                    Logistics Policy Benefits
                  </Link>
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-5">
              <Reveal delay={0.2}>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { icon: MapPin, label: "Serves Delhi-NCR & Hills" },
                    { icon: Warehouse, label: "Cold Storage Permitted" },
                    { icon: IndianRupee, label: "20% Capital Subsidy" },
                    { icon: TrendingDown, label: "Low Land Entry Cost" },
                  ].map((c, i) => (
                    <div key={i} className="border border-white/[0.08] bg-[#1E293B] p-5 hover:border-orange-500/40 transition-colors" data-testid={`warehouse-highlight-${i}`}>
                      <c.icon className="w-6 h-6 text-orange-500 mb-3" strokeWidth={1.5} />
                      <div className="text-slate-300 text-sm font-medium leading-snug">{c.label}</div>
                    </div>
                  ))}
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ UTTARAKHAND INDUSTRIAL INCENTIVES GUIDE ═══════ */}
      <section className="relative py-24 border-t border-white/10" data-testid="seo-incentives-section">
        <div className="absolute inset-0 blueprint-grid opacity-10 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <Reveal>
            <span className="overline inline-flex items-center gap-2">
              <Landmark className="w-3.5 h-3.5 text-orange-500" /> Government Incentives
            </span>
            <div className="section-divider-left mt-3" />
            <h2 className="text-white mt-4" style={headingStyle}>
              Uttarakhand Industrial Incentives Guide
            </h2>
            <p className="text-slate-400 text-sm mt-4 max-w-3xl leading-relaxed">
              Setting up a unit at IIE Sigaddi, Kotdwar unlocks a stack of benefits under the <strong className="text-slate-200">Uttarakhand
              Industrial Policy</strong> and the <strong className="text-slate-200">Uttarakhand Logistics Policy 2023</strong>. Investors from
              across India — including non-residents of Uttarakhand — can acquire SIIDCUL / SIIDCUL industrial plots for a bona-fide
              industrial or commercial project.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 mt-10">
            {incentives.map((item, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className="flex items-start gap-3 border-b border-white/[0.06] pb-4" data-testid={`incentive-item-${i}`}>
                  <CheckCircle2 className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" strokeWidth={1.75} />
                  <span className="text-slate-300 text-sm leading-relaxed">{item}</span>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.2}>
            <div className="mt-10">
              <Link
                to="/schemes"
                className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 font-bold uppercase tracking-wider text-sm transition-all duration-300 hover:shadow-[0_0_30px_rgba(249,115,22,0.3)]"
                data-testid="incentives-explore-btn"
              >
                Explore All Schemes & Policies <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
